import { Fragment, useId, type ReactNode } from "react";

import { I } from "./icons";
import "./Question.css";

/**
 * The question screens' parts (SNIPPETS §28 "Question option row", §36 "Own-addition chip and
 * large search field"). Wrap the page in `.k-large` so they run at the large scale.
 *
 * When to use: a screen that asks one question whose every answer changes something — the food
 * questionnaire. Not for settings forms (those are `FieldSet`s) or a 2–4-way view switch
 * (`Segmented`).
 */

/** "Question 2 of 13" over an 8 px bar; `label` instead for a step that is not counted. */
export function QuestionProgress({ n, of, label }: { n?: number; of?: number; label?: string }) {
  const text = label ?? `Question ${n} of ${of}`;
  const pct = n !== undefined && of ? Math.round((n / of) * 1000) / 10 : 100;
  return (
    <div className="k-qprog">
      <p className="k-qprog__txt">{text}</p>
      <div className="k-qprog__bar" role="img" aria-label={text}>
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** The question (24/600) with an optional helper line; `second` for a question sharing the screen. */
export function QuestionHead({
  id,
  title,
  help,
  second,
}: {
  id: string;
  title: ReactNode;
  help?: ReactNode;
  second?: boolean;
}) {
  const Tag = second ? "h2" : "h1";
  return (
    <div className="k-q__head">
      <Tag id={id} className={`k-q__title${second ? " k-q__title--second" : ""}`}>
        {title}
      </Tag>
      {help ? <p className="k-q__help">{help}</p> : null}
    </div>
  );
}

export interface QuestionOption<T extends string> {
  value: T;
  label: string;
  sub?: string;
  /** shown but not choosable; say why in `sub` or nearby */
  disabled?: boolean;
}

/**
 * One question's answers as whole-row options — radios for `single`, checkboxes otherwise. A
 * chosen row takes the tint; `follow` opens a follow-up inside that tint, directly under the row.
 */
export function QuestionOptions<T extends string>({
  labelledBy,
  options,
  chosen,
  onPick,
  single,
  follow,
}: {
  labelledBy: string;
  options: readonly QuestionOption<T>[];
  chosen: readonly T[];
  onPick: (value: T) => void;
  single?: boolean;
  follow?: (value: T) => ReactNode;
}) {
  const name = useId();
  return (
    <div role={single ? "radiogroup" : "group"} aria-labelledby={labelledBy} className="k-qopts">
      {options.map((o) => {
        const on = chosen.includes(o.value);
        const id = `${name}-${o.value}`;
        const more = on && follow ? follow(o.value) : null;
        return (
          <Fragment key={o.value}>
            <label htmlFor={id} className={`k-qopt${on ? " is-on" : ""}${o.disabled ? " is-off" : ""}`}>
              <input
                id={id}
                type={single ? "radio" : "checkbox"}
                name={name}
                checked={on}
                disabled={o.disabled}
                onChange={() => onPick(o.value)}
              />
              <span className="k-qopt__txt">
                <span className="k-qopt__title">{o.label}</span>
                {o.sub ? <span className="k-qopt__sub">{o.sub}</span> : null}
              </span>
            </label>
            {more}
          </Fragment>
        );
      })}
    </div>
  );
}

/** A follow-up inside the chosen row's tint: its count, its question, the control, one sentence. */
export function QuestionFollowUp({
  count,
  question,
  children,
  line,
}: {
  count?: string;
  question: ReactNode;
  children?: ReactNode;
  line?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="k-qfollow" role="group" aria-labelledby={id}>
      <div className="k-qfollow__head">
        {count ? <p className="k-qfollow__count">{count}</p> : null}
        <h2 id={id} className="k-qfollow__q">
          {question}
        </h2>
      </div>
      {children}
      {line ? <p className="k-qfollow__line">{line}</p> : null}
    </div>
  );
}

/** The large search field (kit §36): a visible label, the search icon, a 17/600 input. */
export function QuestionSearch({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const id = useId();
  return (
    <div className="k-qsearch">
      <label htmlFor={id} className="k-qsearch__label">
        {label}
      </label>
      <div className="k-qsearch__box">
        {I.search}
        <input
          id={id}
          type="search"
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

/** "How your day is split": the shares as one bar, then each meal's share and kcal. */
export function DaySplitView({
  rows,
  unplanned,
  note,
}: {
  rows: { label: string; pct: number; kcal: number }[];
  unplanned?: number;
  note?: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="k-group">
      <h2 id={id} className="k-group__h">
        How your day is split
      </h2>
      <div className="k-qsplit">
        <div className="k-qsplit__bar" aria-hidden="true">
          {rows.map((r) => (
            <span key={r.label} style={{ flex: `${r.kcal} 1 0` }} />
          ))}
          {unplanned ? <span className="is-gap" style={{ flex: `${unplanned} 1 0` }} /> : null}
        </div>
        <dl className="k-qsplit__grid">
          {rows.map((r) => (
            <div key={r.label} className="k-qsplit__cell">
              <dt>{r.label}</dt>
              <dd>
                <span className="k-qsplit__pct">{r.pct} %</span>
                <span className="k-qsplit__kcal">~{r.kcal.toLocaleString()} kcal</span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
      {note ? <p className="k-qsplit__note">{note}</p> : null}
    </section>
  );
}
